import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getInvoicesSummary,
  getSubscriptionsSummary,
  getIncomeVsExpenses,
  getIncomeExpensesMonthDetail,
  getCashMovement,
  getMrrVsExpenses,
  getRevenueVsExpenses,
  getMrrMonth,
  getToolCosts,
  getVendorMapping,
  saveVendorMapping,
} from '../api/finance';
import type { InvoiceRange } from '../api/finance';

/** `months` (last N months, this one included) overrides `range` for the invoiced/collected totals. */
export function useInvoicesSummary(range: InvoiceRange = 'all', months?: number, fullMonths = false) {
  return useQuery({
    queryKey: ['finance', 'invoices-summary', range, months ?? null, fullMonths],
    queryFn: () => getInvoicesSummary(range, months, fullMonths),
    placeholderData: (prev) => prev, // keep tiles stable while a new range loads
    refetchInterval: 3_600_000, // every hour — data refreshes daily from Odoo
  });
}

export function useSubscriptionsSummary() {
  return useQuery({
    queryKey: ['finance', 'subscriptions-summary'],
    queryFn: getSubscriptionsSummary,
    refetchInterval: 3_600_000,
  });
}

export function useIncomeVsExpenses(months?: number) {
  return useQuery({
    queryKey: ['finance', 'income-expenses', months],
    queryFn: () => getIncomeVsExpenses(months),
    refetchInterval: 3_600_000,
  });
}

export function useIncomeExpensesMonthDetail(month: string | null) {
  return useQuery({
    queryKey: ['finance', 'income-expenses-month-detail', month],
    queryFn: () => getIncomeExpensesMonthDetail(month!),
    enabled: month !== null,
  });
}

export function useCashMovement(months?: number) {
  return useQuery({
    queryKey: ['finance', 'cash-movement', months],
    queryFn: () => getCashMovement(months),
    refetchInterval: 3_600_000,
  });
}

export function useMrrVsExpenses(months?: number) {
  return useQuery({
    queryKey: ['finance', 'mrr-vs-expenses', months],
    queryFn: () => getMrrVsExpenses(months),
    refetchInterval: 3_600_000,
  });
}

export function useRevenueVsExpenses(months?: number) {
  return useQuery({
    queryKey: ['finance', 'revenue-vs-expenses', months],
    queryFn: () => getRevenueVsExpenses(months),
  });
}

/** One month of MRR, one row per customer. */
export function useMrrMonth(month: string | null) {
  return useQuery({
    queryKey: ['finance', 'mrr-month', month],
    queryFn: () => getMrrMonth(month!),
    enabled: month !== null,
  });
}

export function useToolCosts(months?: number) {
  return useQuery({
    queryKey: ['finance', 'tool-costs', months],
    queryFn: () => getToolCosts(months),
    // House copy has no em dashes and says months as "Aug 26"; API builds before
    // that change still send "IT — Product" and "in Aug 2026 — 33% above".
    select: tidyToolCostCopy,
    placeholderData: (prev) => prev,
    refetchInterval: 3_600_000,
    // A timed-out request is retried once, not three times: four 30-second
    // waits read as a hung page rather than an error.
    retry: 1,
  });
}

export function useVendorMapping() {
  return useQuery({
    queryKey: ['finance', 'tool-costs', 'mapping'],
    queryFn: getVendorMapping,
  });
}

/** Assigning a vendor changes how spend is bucketed, so the report is refetched. */
export function useSaveVendorMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveVendorMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'tool-costs'] });
    },
  });
}

const MONTH_NAMES = 'Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec';

/** "IT — Product" → "IT, product"; "in Aug 2026 — 33% above" → "in Aug 26, 33% above". Pure. */
export function tidyFinanceCopy(text: string): string {
  return text
    .replace(/^IT — (\w)/, (_m, c: string) => `IT, ${c.toLowerCase()}`)
    .replace(/ — /g, ', ')
    .replace(new RegExp(`\\b(${MONTH_NAMES}) 20(\\d{2})\\b`, 'g'), '$1 $2');
}

function tidyToolCostCopy(data: Awaited<ReturnType<typeof getToolCosts>>) {
  return {
    ...data,
    categories: data.categories.map((c) => ({ ...c, label: tidyFinanceCopy(c.label) })),
    insights: data.insights.map((i) => ({ ...i, message: tidyFinanceCopy(i.message) })),
  };
}
